import { SignJWT, calculateJwkThumbprint, exportJWK } from "jose";

type StoredDpopKey = {
  privateKey: CryptoKey;
  publicKey: CryptoKey;
  publicJwk: JsonWebKey;
  thumbprint: string;
};

const DB_NAME = "codessa-cloud";
const STORE_NAME = "dpop";
const KEY_PREFIX = "browser-key:";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open browser key storage."));
  });
}

async function readStored(keyName: string): Promise<StoredDpopKey | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(keyName);
    request.onsuccess = () => resolve((request.result as StoredDpopKey | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error("Could not read browser key."));
  });
}

async function writeStored(value: StoredDpopKey, keyName: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).put(value, keyName);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("Could not persist browser key."));
  });
}

export async function clearDpopKey(accountId = "anonymous"): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const request = db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).delete(`${KEY_PREFIX}${accountId}`);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error("Could not clear browser key."));
  });
}

export class BrowserDpopKey {
  private constructor(private readonly stored: StoredDpopKey) {}

  static async load(accountId = "anonymous"): Promise<BrowserDpopKey> {
    const existing = await readStored(`${KEY_PREFIX}${accountId}`);
    if (existing) return new BrowserDpopKey(existing);
    const pair = await crypto.subtle.generateKey(
      { name: "ECDSA", namedCurve: "P-256" },
      true,
      ["sign", "verify"],
    );
    const publicJwk = await exportJWK(pair.publicKey);
    publicJwk.alg = "ES256";
    publicJwk.use = "sig";
    const thumbprint = await calculateJwkThumbprint(publicJwk, "sha256");
    const stored = {
      privateKey: pair.privateKey,
      publicKey: pair.publicKey,
      publicJwk,
      thumbprint,
    } satisfies StoredDpopKey;
    await writeStored(stored, `${KEY_PREFIX}${accountId}`);
    return new BrowserDpopKey(stored);
  }

  get thumbprint(): string {
    return this.stored.thumbprint;
  }

  async proof(method: string, url: string, nonce?: string, accessToken?: string): Promise<string> {
    const normalized = new URL(url);
    normalized.hash = "";
    const payload: Record<string, string | number> = {
      htu: normalized.toString(),
      htm: method.toUpperCase(),
      iat: Math.floor(Date.now() / 1000),
      jti: crypto.randomUUID(),
    };
    if (nonce) payload.nonce = nonce;
    if (accessToken) {
      const bytes = new TextEncoder().encode(accessToken);
      const digest = await crypto.subtle.digest("SHA-256", bytes);
      payload.ath = base64Url(new Uint8Array(digest));
    }
    return new SignJWT(payload)
      .setProtectedHeader({ typ: "dpop+jwt", alg: "ES256", jwk: this.stored.publicJwk })
      .sign(this.stored.privateKey);
  }
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}
